import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { History } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { agents } from "@/lib/mockData";
import { useStore } from "@/lib/store";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  createDebate,
  getDebate,
  listDebates,
  streamDebate,
} from "@/lib/debateApi";
import AgentCard from "./AgentCard";
import DecisionSummaryCard from "./DecisionSummaryCard";
import DebateFeed from "./DebateFeed";
import GoalInput from "./GoalInput";

const fmtMMSS = (totalSeconds) => {
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m} min ${s.toString().padStart(2, "0")}s`;
};

export default function BoardroomPage() {
  const mode = useStore((s) => s.mode);
  const profile = useStore((s) => s.profile);
  const agentsEnabled = useStore((s) => s.agentsEnabled);
  const isStreaming = useStore((s) => s.isStreamingDebate);
  const setIsStreaming = useStore((s) => s.setIsStreamingDebate);
  const currentSessionId = useStore((s) => s.currentDebateSessionId);
  const setCurrentSessionId = useStore((s) => s.setCurrentDebateSessionId);
  const setStreamingAgentId = useStore((s) => s.setCurrentlyStreamingAgentId);
  const streamingAgentId = useStore((s) => s.currentlyStreamingAgentId);

  const modeLabel = mode === "bootstrap" ? "Bootstrapping" : "Funding";

  const [elapsed, setElapsed] = useState(0);
  const [activeGoal, setActiveGoal] = useState("");
  const [messages, setMessages] = useState([]); // {agentId, name, role, content, streaming}
  const [decision, setDecision] = useState(null);
  const [decisionLive, setDecisionLive] = useState(false);
  const [pastDebates, setPastDebates] = useState([]);
  const streamRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Live ticking timer (resets per session)
  useEffect(() => {
    if (!isStreaming) return;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [isStreaming]);

  // Refresh past-debates list whenever streaming flips false (just-finished one shows up).
  const refreshList = useCallback(async () => {
    try {
      const list = await listDebates(20);
      setPastDebates(list || []);
    } catch {
      /* swallow */
    }
  }, []);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  // On mount, load the most recent COMPLETE debate's decision so the right
  // column isn't empty for returning users. If `?debate=<id>` is present
  // (e.g. forwarded from a 1:1 chat), load THAT debate instead.
  useEffect(() => {
    const deepLinkId = searchParams.get("debate");
    if (deepLinkId) {
      // Defer to loadPastDebate which streams (or replays) the session.
      loadPastDebate(deepLinkId);
      // Clean the URL so subsequent navigations don't re-trigger.
      const next = new URLSearchParams(searchParams);
      next.delete("debate");
      setSearchParams(next, { replace: true });
      return;
    }
    (async () => {
      try {
        const list = await listDebates(20);
        const last = (list || []).find((d) => d.status === "complete");
        if (last) {
          const detail = await getDebate(last.session_id);
          if (detail?.decision) {
            setDecision(detail.decision);
            setDecisionLive(false);
          }
        }
      } catch {
        /* swallow */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (goal) => {
    setMessages([]);
    setDecision(null);
    setDecisionLive(false);
    setActiveGoal(goal);
    setElapsed(0);
    const enabledIds = Object.entries(agentsEnabled)
      .filter(([, v]) => v !== false)
      .map(([k]) => k);
    const sess = await createDebate(goal, {
      enabled_agent_ids: enabledIds,
      mode,
    });
    setCurrentSessionId(sess.session_id);
    setIsStreaming(true);
    startStream(sess.session_id);
  };

  const startStream = (sessionId) => {
    streamRef.current?.close();
    streamRef.current = streamDebate(sessionId, {
      onSessionStart: ({ goal }) => {
        if (goal) setActiveGoal(goal);
      },
      onAgentStart: ({ agent_id, name, role, sequence }) => {
        setStreamingAgentId(agent_id);
        setMessages((prev) => [
          ...prev,
          { agentId: agent_id, name, role, content: "", streaming: true, sequence },
        ]);
      },
      onAgentToken: ({ agent_id, token }) => {
        setMessages((prev) => {
          const next = [...prev];
          for (let i = next.length - 1; i >= 0; i--) {
            if (next[i].agentId === agent_id && next[i].streaming) {
              next[i] = { ...next[i], content: (next[i].content || "") + token };
              break;
            }
          }
          return next;
        });
      },
      onAgentEnd: ({ agent_id, content }) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.agentId === agent_id && m.streaming
              ? { ...m, content: content ?? m.content, streaming: false }
              : m,
          ),
        );
        setStreamingAgentId(null);
      },
      onDecisionStart: () => {
        setStreamingAgentId("decision");
      },
      onDecisionEnd: ({ decision }) => {
        if (decision) {
          setDecision(decision);
          setDecisionLive(true);
        }
        setStreamingAgentId(null);
      },
      onTasksGenerated: ({ count }) => {
        if (count > 0) {
          toast.success(`${count} task${count === 1 ? "" : "s"} generated from the debate`, {
            description: "Open Tasks to pick them up.",
          });
        }
      },
      onCancelled: () => {
        toast.message("Debate cancelled.");
        setIsStreaming(false);
        setStreamingAgentId(null);
        refreshList();
      },
      onError: ({ detail }) => {
        toast.error("Debate error", { description: detail || "" });
        setIsStreaming(false);
        setStreamingAgentId(null);
        refreshList();
      },
      onComplete: () => {
        setIsStreaming(false);
        setStreamingAgentId(null);
        refreshList();
      },
      onClose: () => {
        // Stream socket closed (could be after complete/cancelled/error).
      },
    });
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => streamRef.current?.close();
  }, []);

  const loadPastDebate = async (sessionId) => {
    streamRef.current?.close();
    setMessages([]);
    setDecision(null);
    setDecisionLive(false);
    setCurrentSessionId(sessionId);
    setIsStreaming(true);
    setElapsed(0);
    // Replay via stream endpoint (server replays at faster cadence)
    startStream(sessionId);
    // Also fetch the goal so the feed header shows it
    try {
      const detail = await getDebate(sessionId);
      setActiveGoal(detail?.session?.goal || "");
    } catch {
      /* swallow */
    }
  };

  const firstRow = useMemo(() => agents.slice(0, 4), []);
  const secondRow = useMemo(() => agents.slice(4, 8), []);

  return (
    <div data-testid="boardroom-page" className="px-8 pb-16 pt-8 lg:px-10">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className="flex items-end justify-between gap-8"
      >
        <div>
          <div className="eyebrow" data-testid="boardroom-eyebrow">
            <span data-testid="boardroom-mode-subtitle">{modeLabel} mode</span>
            <span className="mx-1.5 text-zinc-300">·</span>
            Executive Boardroom
          </div>
          <h1 className="mt-3 font-extrabold leading-[0.95] tracking-[-0.04em]">
            <span className="block text-[64px] text-zinc-900 lg:text-[88px] xl:text-[104px]">
              Eight minds.
            </span>
            <span className="block text-[64px] text-zinc-300 lg:text-[88px] xl:text-[104px]">
              {profile?.name || "One company"}.
            </span>
          </h1>
        </div>

        <div className="mb-3 flex items-center gap-3">
          {/* Past debates */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="past-debates-trigger"
                className="card-soft inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12.5px] font-semibold text-zinc-700 transition-colors hover:bg-zinc-50"
              >
                <History className="h-3.5 w-3.5" strokeWidth={2.4} />
                Past debates · {pastDebates.length}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[340px] p-0"
              data-testid="past-debates-popover"
            >
              <div className="max-h-[420px] overflow-y-auto">
                {pastDebates.length === 0 ? (
                  <div className="p-5 text-[13px] text-zinc-500">
                    No debates yet — run your first one below.
                  </div>
                ) : (
                  <ul className="divide-y divide-black/[0.05]">
                    {pastDebates.map((d) => (
                      <li key={d.session_id}>
                        <button
                          type="button"
                          data-testid={`past-debate-${d.session_id}`}
                          onClick={() => loadPastDebate(d.session_id)}
                          className="flex w-full flex-col items-start gap-1 px-4 py-3 text-left transition-colors hover:bg-zinc-50"
                        >
                          <div className="flex w-full items-center gap-2">
                            <span className="flex-1 truncate text-[13px] font-semibold text-zinc-900">
                              {d.goal}
                            </span>
                            <StatusPill status={d.status} />
                          </div>
                          <div className="flex w-full items-center gap-2 text-[11px] text-zinc-500">
                            <span>{formatDistanceToNow(new Date(d.created_at))} ago</span>
                            {d.confidence != null && (
                              <span className="font-mono font-semibold text-zinc-700">
                                · {d.confidence}%
                              </span>
                            )}
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </PopoverContent>
          </Popover>

          {/* Live debate chip */}
          {isStreaming && (
            <div
              data-testid="debate-session-chip"
              className="card-soft inline-flex items-center gap-2.5 rounded-full px-4 py-2"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-500/40" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-violet-600" />
              </span>
              <span className="text-[12px] font-semibold text-zinc-700">
                Debate session active
              </span>
              <span className="text-[12px] text-zinc-400">·</span>
              <span
                data-testid="debate-session-timer"
                className="font-mono text-[12px] font-semibold text-zinc-900"
              >
                {fmtMMSS(elapsed)}
              </span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Bento grid */}
      <div
        data-testid="boardroom-bento"
        className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5"
      >
        {firstRow.map((agent, i) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            index={i}
            isActive={streamingAgentId === agent.id}
            isEnabled={agentsEnabled[agent.id] !== false}
          />
        ))}

        <div className="md:col-span-2 lg:col-span-4 xl:col-span-1 xl:row-span-2">
          <DecisionSummaryCard decision={decision} isLive={decisionLive} />
        </div>

        {secondRow.map((agent, i) => (
          <AgentCard
            key={agent.id}
            agent={agent}
            index={i + 4}
            isActive={streamingAgentId === agent.id}
            isEnabled={agentsEnabled[agent.id] !== false}
          />
        ))}
      </div>

      {/* Goal Input */}
      <div className="mt-8">
        <GoalInput onCreate={handleCreate} />
      </div>

      {/* Debate Feed */}
      <div className="mt-6">
        <DebateFeed messages={messages} goal={activeGoal} isStreaming={isStreaming} />
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    complete: "bg-emerald-50 text-emerald-700",
    streaming: "bg-violet-50 text-violet-700",
    cancelled: "bg-zinc-100 text-zinc-600",
    failed: "bg-rose-50 text-rose-700",
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.16em] ${styles[status] || "bg-zinc-100 text-zinc-600"}`}
    >
      {status}
    </span>
  );
}
